'use client';

import { supabase } from './supabase';

/**
 * Client-side fetch interceptor that auto-attaches an
 * `Authorization: Bearer <supabase session access_token>` header
 * (validated server-side — cryptographically signed, NOT spoofable) to
 * every same-origin `/api/*` request.
 *
 * Why this exists
 * ----------------
 * Plain `fetch()` doesn't add an Authorization header, and Supabase JS
 * stores the session in localStorage (not cookies), so without this patch
 * a call from any desktop/mobile page would fail the server auth gates
 * (`requireAdmin`, `requireSuperAdmin`, `requirePlatformAdmin`,
 * `resolveAuthContext`, and any handler that reads the caller via `ctx`).
 * This installs ONE patch on `window.fetch` at app boot that attaches the
 * Bearer to every same-origin `/api/*` request, unblocking every fetch
 * site without touching the call sites.
 *
 * History — the removed `callerAuthId` param
 * ------------------------------------------
 * Until 2026-06-21 this also appended an unsigned
 * `callerAuthId=<auth_user_id>` URL param as a fallback for transient
 * token states (added 2026-05-21). That param was SPOOFABLE — a logged-in
 * user could pass an admin's auth_user_id and reach admin gates — so it
 * (and the matching server-side fallback in resolveAuthContext) was
 * removed. Identity now travels ONLY in the signed Bearer. Public /
 * no-session surfaces that call gated routes attach a Bearer explicitly
 * (see app/lib/authedFetch.ts and the daily-scheduler relief pages).
 *
 * Properties
 * ----------
 *  - SSR-safe (no-op when `window` is undefined).
 *  - Idempotent (`installFetchAuth()` can be called many times).
 *  - Does NOT override an explicit `Authorization` header set by the caller.
 *  - Same-origin only — never leaks the token to external hosts
 *    (Supabase, Resend, third-party APIs).
 *  - Best-effort — any exception in the patched code falls through to
 *    the original fetch, so it never breaks the call.
 */

let currentAuthUserId: string | null = null;
let installed = false;

/**
 * Register the active auth user id. Retained for callers (AuthContext +
 * MobileAuthContext) that record the session id here; the Bearer the
 * interceptor attaches is read live from `supabase.auth.getSession()` and
 * does NOT depend on this value. (It no longer drives any URL-param
 * injection — that path was removed 2026-06-21.)
 */
export function setFetchAuthUserId(authUserId: string | null): void {
  currentAuthUserId = authUserId || null;
}

export function getFetchAuthUserId(): string | null {
  return currentAuthUserId;
}

/**
 * Patch `window.fetch`. Idempotent; safe to call from multiple contexts.
 */
export function installFetchAuth(): void {
  if (installed) return;
  if (typeof window === 'undefined') return;

  const originalFetch = window.fetch.bind(window);

  // Read the current Supabase access_token. Returns null if no session
  // (anonymous), if the session is being refreshed, or if the call
  // errors. Best-effort — callers must tolerate a missing Bearer.
  const getAccessToken = async (): Promise<string | null> => {
    try {
      const { data } = await supabase.auth.getSession();
      return data?.session?.access_token ?? null;
    } catch {
      return null;
    }
  };

  // Determine whether an input URL refers to a same-origin /api/ route.
  const isSameOriginApi = (urlStr: string): boolean => {
    if (urlStr.startsWith('/api/')) return true;
    if (urlStr.startsWith(window.location.origin + '/api/')) return true;
    return false;
  };

  window.fetch = async function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    try {
      let urlStr: string;
      if (typeof input === 'string') urlStr = input;
      else if (input instanceof URL) urlStr = input.toString();
      else if (input && typeof (input as Request).url === 'string') urlStr = (input as Request).url;
      else return originalFetch(input as RequestInfo, init);

      if (!isSameOriginApi(urlStr)) {
        return originalFetch(input as RequestInfo, init);
      }

      // ── Bearer header injection (validated server-side; NOT spoofable) ──
      // The unsigned `?callerAuthId=` URL-param injection was REMOVED on
      // 2026-06-21 — it let a logged-in user spoof an admin's auth_user_id.
      // Identity now travels ONLY in this cryptographically-signed Bearer.
      // Don't override an explicit Authorization header the caller set.
      const baseHeaders = new Headers(
        (input instanceof Request ? input.headers : undefined) || init?.headers || undefined,
      );
      if (!baseHeaders.has('authorization') && !baseHeaders.has('Authorization')) {
        const token = await getAccessToken();
        if (token) baseHeaders.set('Authorization', `Bearer ${token}`);
      }

      const mergedInit: RequestInit = { ...(init || {}), headers: baseHeaders };

      if (input instanceof Request) {
        return originalFetch(input as RequestInfo, mergedInit);
      }
      return originalFetch(urlStr, mergedInit);
    } catch {
      return originalFetch(input as RequestInfo, init);
    }
  };

  // ── Session-cookie mirror (auth for browser NAVIGATIONS) ────────────
  // The Bearer patch above only covers fetch(). Browser navigations —
  // <a href>, <iframe src>, <img src> pointing at /api/* serve routes
  // (admin-documents / task-attachments downloads) — send no header, and
  // Supabase JS keeps the session in localStorage only, so those requests
  // reached resolveAuthContext anonymous and 401'd once the storage
  // buckets went private. Mirror the access_token into `sb-access-token`
  // — a cookie name resolveAuthContext already parses and verifies via
  // supabase.auth.getUser(), the same non-spoofable validation as the
  // Bearer. Scoped Path=/, SameSite=Lax (sent on same-origin subresource
  // loads + top-level GET navigations only), expires with the token,
  // cleared on sign-out (any tab — Supabase rebroadcasts auth events).
  const syncSessionCookie = (
    session: { access_token?: string | null; expires_at?: number | null } | null,
  ): void => {
    try {
      if (typeof document === 'undefined') return;
      const token = session?.access_token ?? null;
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      if (!token) {
        document.cookie = `sb-access-token=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
        return;
      }
      const nowSec = Math.floor(Date.now() / 1000);
      const maxAge = Math.min(
        Math.max((session?.expires_at ?? nowSec + 3600) - nowSec, 60),
        60 * 60 * 24,
      );
      document.cookie = `sb-access-token=${token}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
    } catch {
      // Best-effort — a failed cookie write degrades to pre-cookie behavior
      // (fetch() calls still authenticate via the Bearer).
    }
  };

  void supabase.auth
    .getSession()
    .then(({ data }) => syncSessionCookie(data?.session ?? null))
    .catch(() => {});
  supabase.auth.onAuthStateChange((_event, session) => syncSessionCookie(session));

  installed = true;
}
